import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { TRANSLATIONS, LANGUAGES, detectLanguage, format } from "./translations";
import { CURRENCIES, detectCurrency, fmt } from "./currency";

const STORAGE_KEY = "ppg_lang";
const CCY_KEY = "ppg_ccy";
const MODAL_SEEN_KEY = "ppg_lang_modal_seen";

const Ctx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState("en");
  const [ccy, setCcyState] = useState("USD");
  const [showModal, setShowModal] = useState(false);
  const [suggested, setSuggested] = useState("en");

  useEffect(() => {
    const detected = detectLanguage();
    const detCcy = detectCurrency();
    setSuggested(detected);

    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get("lang");
    const urlCcy = params.get("ccy");

    const savedLang = urlLang && TRANSLATIONS[urlLang] ? urlLang : localStorage.getItem(STORAGE_KEY);
    const savedCcy = urlCcy && CURRENCIES[urlCcy] ? urlCcy : localStorage.getItem(CCY_KEY);

    const finalCcy = savedCcy && CURRENCIES[savedCcy] ? savedCcy : detCcy;
    setCcyState(finalCcy);
    localStorage.setItem(CCY_KEY, finalCcy);

    if (savedLang && TRANSLATIONS[savedLang]) {
      setLangState(savedLang);
      document.documentElement.lang = savedLang;
      if (urlLang || localStorage.getItem(MODAL_SEEN_KEY)) return;
    }

    if (!localStorage.getItem(MODAL_SEEN_KEY)) {
      // Only show modal when we've detected a non-English regional language
      if (detected !== "en") {
        setShowModal(true);
      } else {
        localStorage.setItem(MODAL_SEEN_KEY, "1");
      }
    }
  }, []);

  const setLang = useCallback((code) => {
    if (!TRANSLATIONS[code]) return;
    setLangState(code);
    localStorage.setItem(STORAGE_KEY, code);
    localStorage.setItem(MODAL_SEEN_KEY, "1");
    setShowModal(false);
    document.documentElement.lang = code;
  }, []);

  const setCcy = useCallback((code) => {
    if (!CURRENCIES[code]) return;
    setCcyState(code);
    localStorage.setItem(CCY_KEY, code);
  }, []);

  const dismissModal = useCallback(() => {
    localStorage.setItem(MODAL_SEEN_KEY, "1");
    setShowModal(false);
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
      const str = dict[key] ?? TRANSLATIONS.en[key] ?? key;
      return vars ? format(str, vars) : str;
    },
    [lang]
  );

  const currency = useMemo(() => CURRENCIES[ccy] || CURRENCIES.USD, [ccy]);
  const fmtCcy = useCallback((n) => fmt(n, ccy), [ccy]);

  return (
    <Ctx.Provider value={{
      lang, setLang, t, languages: LANGUAGES,
      ccy, setCcy, currency, fmtCcy, currencies: CURRENCIES,
      showModal, setShowModal, dismissModal, suggested,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useI18n() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n must be used inside I18nProvider");
  return v;
}
